/**
 * API Client
 *
 * HTTP client with two-header authentication.
 */

import { API_CONFIG } from '../utils/constants';
import {
  ApiError,
  ERROR_MESSAGES,
  RETRYABLE_STATUS_CODES,
} from '../types/api.types';
import { withRetry, isRetryableError } from './retry';
import { logger } from '../utils/logger';

const apiGatewayUrl = import.meta.env.VITE_API_GATEWAY_URL;
if (!apiGatewayUrl || !apiGatewayUrl.startsWith('https://')) {
  throw new Error(
    `VITE_API_GATEWAY_URL is missing or invalid — must start with https:// (got: ${apiGatewayUrl})`
  );
}
logger.info('API Gateway URL configured', { url: apiGatewayUrl });

/**
 * Per-request configuration.
 */
export interface RequestConfig {
  /**
   * Whether to retry the request on transient failures (5xx / timeout / network).
   * Defaults to true. Set to false for non-idempotent operations such as payment
   * submission, where an automatic retry would resubmit the same request (PAS-32663).
   */
  retry?: boolean;
}

/**
 * API Client Class
 *
 * Handles all HTTP requests with:
 * - Two-header authentication (x-api-key + x-session-id)
 * - Error handling with retry logic
 * - Request/response logging
 */
export class ApiClient {
  private baseUrl: string;
  private apiKey: string | null = null;
  private sessionId: string | null = null;
  private timeout: number;

  /**
   * Create API client
   *
   * @param baseUrl - Base URL for API requests (e.g., "/bff/agent")
   * @param timeout - Request timeout in milliseconds (default: 30000)
   */
  constructor(baseUrl: string, timeout: number = API_CONFIG.TIMEOUT_MS) {
    this.baseUrl = baseUrl;
    this.timeout = timeout;
  }

  /**
   * Set authentication credentials
   *
   * Called after initial authentication to store apiKey and sessionId
   * SECURITY: Stored in memory only, NOT in localStorage
   *
   * @param apiKey - Merchant client API key
   * @param sessionId - Session identifier
   */
  setCredentials(apiKey: string, sessionId: string): void {
    this.apiKey = apiKey;
    this.sessionId = sessionId;
    logger.debug('API credentials set', {
      hasApiKey: !!apiKey,
      hasSessionId: !!sessionId,
    });
  }

  /**
   * Clear authentication credentials
   *
   * Called on logout or session expiry
   */
  clearCredentials(): void {
    this.apiKey = null;
    this.sessionId = null;
    logger.debug('API credentials cleared');
  }

  /**
   * Check if client is authenticated
   *
   * @returns true if credentials are set, false otherwise
   */
  isAuthenticated(): boolean {
    return this.apiKey !== null && this.sessionId !== null;
  }

  /**
   * Make authenticated API request with retry logic
   *
   * @param endpoint - API endpoint (e.g., "/payments/123/submit")
   * @param options - Fetch options
   * @returns Response data
   *
   * @throws {ApiError} On request failure after retries
   */
  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    config: RequestConfig = {}
  ): Promise<{ data: T }> {
    // Build full URL
    const url = `${this.baseUrl}${endpoint}`;

    // Build headers with authentication
    const headers = this.buildHeaders(options.headers, options.body != null);

    // Create fetch function with timeout
    const fetchWithTimeout = () =>
      this.fetchWithTimeout<T>(url, { ...options, headers });

    try {
      // Execute request with retry logic. Non-idempotent operations (e.g. payment
      // submission) pass retry:false so a transient 5xx/timeout cannot resubmit
      // the same request — see PAS-32663.
      const maxAttempts =
        config.retry === false ? 1 : API_CONFIG.RETRY_ATTEMPTS;
      return await withRetry(fetchWithTimeout, {
        maxAttempts,
        initialDelayMs: API_CONFIG.RETRY_DELAY_MS,
      });
    } catch (error) {
      // Log error
      logger.error('API request failed', error, {
        endpoint,
        method: options.method || 'GET',
      });

      // Re-throw ApiError as-is
      if (error instanceof ApiError) {
        throw error;
      }

      // Wrap unknown errors
      throw new ApiError(
        0,
        'UNKNOWN_ERROR',
        'An unexpected error occurred',
        error
      );
    }
  }

  /**
   * Build request headers with authentication
   *
   * @param customHeaders - Additional headers
   * @returns Headers with authentication
   *
   * @throws {Error} If not authenticated
   */
  private buildHeaders(
    customHeaders?: HeadersInit,
    hasBody = false
  ): HeadersInit {
    const headers: Record<string, string> = {};

    if (hasBody) {
      headers['Content-Type'] = 'application/json';
    }

    // Add custom headers
    if (customHeaders) {
      if (customHeaders instanceof Headers) {
        customHeaders.forEach((value, key) => {
          headers[key] = value;
        });
      } else if (Array.isArray(customHeaders)) {
        customHeaders.forEach(([key, value]) => {
          headers[key] = value;
        });
      } else {
        Object.assign(headers, customHeaders);
      }
    }

    // Add authentication headers — throw immediately if credentials missing (fast-fail)
    if (!this.apiKey || !this.sessionId) {
      logger.error('No session credentials available — request aborted');
      throw new ApiError(
        401,
        'UNAUTHORIZED',
        'No session credentials available'
      );
    }

    headers['x-api-key'] = this.apiKey;
    headers['x-session-id'] = this.sessionId;

    return headers;
  }

  /**
   * Fetch with timeout and response handling
   *
   * @param url - Request URL
   * @param options - Fetch options
   * @returns Response data
   *
   * @throws {ApiError} On error response
   */
  private async fetchWithTimeout<T>(
    url: string,
    options: RequestInit
  ): Promise<{ data: T }> {
    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      // Make request
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle response
      return await this.handleResponse<T>(response);
    } catch (error) {
      clearTimeout(timeoutId);

      // Re-throw ApiError (from handleResponse) — already properly structured
      if (error instanceof ApiError) {
        throw error;
      }

      // Handle timeout
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiError(408, 'REQUEST_TIMEOUT', 'Request timeout', error);
      }

      // Network error
      throw new ApiError(0, 'NETWORK_ERROR', 'Network request failed', error);
    }
  }

  /**
   * Handle response and parse JSON
   *
   * @param response - Fetch response
   * @returns Response data
   *
   * @throws {ApiError} On error response
   */
  private async handleResponse<T>(response: Response): Promise<{ data: T }> {
    // Success response
    if (response.ok) {
      if (response.status === 204) {
        return { data: undefined as unknown as T };
      }
      const data = await response.json();
      return { data };
    }

    // Handle auth errors — clear credentials and surface user-friendly message
    if (response.status === 401) {
      this.clearCredentials();
      logger.error(
        'Authentication failed (401) — credentials cleared. Please refresh the page.'
      );
      throw new ApiError(
        401,
        'UNAUTHORIZED',
        'Authentication failed. Please refresh the page.'
      );
    }

    if (response.status === 410) {
      this.clearCredentials();
      logger.error(
        'Session has expired (410) — credentials cleared. Please refresh the page.'
      );
      throw new ApiError(
        410,
        'SESSION_EXPIRED',
        'Session has expired. Please refresh the page.'
      );
    }

    // Error response - parse error details
    const errorBody = await this.parseErrorResponse(response);

    // Create ApiError
    const apiError = new ApiError(
      response.status,
      errorBody.errorCode || `HTTP_${response.status}`,
      errorBody.message || this.getDefaultErrorMessage(response.status),
      errorBody.details
    );

    // Check if error is retryable
    if (!isRetryableError(apiError, RETRYABLE_STATUS_CODES)) {
      // Non-retryable error, throw immediately
      throw apiError;
    }

    // Retryable error, will be retried by withRetry
    throw apiError;
  }

  /**
   * Parse error response body
   *
   * @param response - Error response
   * @returns Parsed error body
   */
  private async parseErrorResponse(
    response: Response
  ): Promise<
    Partial<{ errorCode: string; message: string; details: unknown }>
  > {
    try {
      const body = await response.json();
      // Backend format: { errors: [{ type, code, message, param }] }
      if (Array.isArray(body.errors) && body.errors.length > 0) {
        const first = body.errors[0];
        return {
          errorCode: typeof first.code === 'string' ? first.code : undefined,
          message:
            typeof first.message === 'string' ? first.message : undefined,
          details: body,
        };
      }
      return { ...body, details: body };
    } catch {
      // Failed to parse JSON, return empty object
      return {};
    }
  }

  /**
   * Get default error message for status code
   *
   * @param statusCode - HTTP status code
   * @returns User-friendly error message
   */
  private getDefaultErrorMessage(statusCode: number): string {
    const errorMessage = ERROR_MESSAGES[statusCode];
    if (errorMessage) {
      return errorMessage.message;
    }

    // Fallback messages
    if (statusCode >= 500) {
      return 'Server error, please try again';
    }

    if (statusCode >= 400) {
      return 'Request failed, please check your input';
    }

    return 'An unexpected error occurred';
  }

  /**
   * GET request helper
   *
   * @param endpoint - API endpoint
   * @returns Response data
   */
  async get<T>(endpoint: string): Promise<{ data: T }> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  /**
   * POST request helper
   *
   * @param endpoint - API endpoint
   * @param body - Request body
   * @returns Response data
   */
  async post<T>(
    endpoint: string,
    body?: unknown,
    config?: RequestConfig
  ): Promise<{ data: T }> {
    return this.request<T>(
      endpoint,
      {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      },
      config
    );
  }

  /**
   * PATCH request helper
   *
   * @param endpoint - API endpoint
   * @param body - Request body
   * @returns Response data
   */
  async patch<T>(endpoint: string, body?: unknown): Promise<{ data: T }> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * DELETE request helper
   *
   * @param endpoint - API endpoint
   * @returns Response data
   */
  async delete<T>(endpoint: string): Promise<{ data: T }> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  /**
   * Get base URL (used for initial authentication)
   *
   * @returns Base URL
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }
}

/**
 * Export ApiError class constructor for external use
 */
export { ApiError };
